// "Coding standards" — the top of Practice Details' Billing Settings tab (David,
// 2026-10-28). How Atlas codes the clinic's visits, kept by Atlas as versioned text and
// opened as a PDF the server draws from it. The clinic sees this section too (it is the
// only part of Billing Settings a read-only user gets); admins also edit it, and every
// save is a new version.
//
// The PDF is fetched as a blob and opened from an object URL, never linked as
// <a href="/api/...">: the Clerk JWT rides only on window.fetch, so a plain navigation
// would arrive unsigned. The tab is opened synchronously on the click (before the fetch)
// so a pop-up blocker treats it as the user's own.

import React, { useEffect, useRef, useState } from "react";

import "./CodingStandardsSection.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const KIND = "coding_standards";

const docUrl = (practiceId, suffix = "") => `${API_URL}/api/practices/${practiceId}/documents/${KIND}${suffix}`;

async function errorFrom(response, fallback) {
  const body = await response.json().catch(() => null);
  return new Error((body && body.error) || fallback);
}

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

// The first lines of the body as plain words, for the page drawn on the thumbnail.
export function previewLines(body, max = 9) {
  return (body || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !/^\|?\s*:?-{2,}/.test(l))
    .slice(0, max)
    .map((l) =>
      l
        .replace(/^#{1,3}\s+/, "")
        .replace(/^[-*]\s+\[[ xX]\]\s+/, "")
        .replace(/^[-*]\s+/, "• ")
        .replace(/\*\*|`/g, "")
        .replace(/\s*\|\s*/g, "  ")
        .trim(),
    );
}

export default function CodingStandardsSection({ practiceId, isAdmin, readOnly }) {
  const [state, setState] = useState({ loaded: false, available: true, doc: null, defaultTitle: "Coding Standards" });
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // {type, text}
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  const canEdit = isAdmin && !readOnly;

  useEffect(() => {
    const forPractice = practiceId;
    setState({ loaded: false, available: true, doc: null, defaultTitle: "Coding Standards" });
    setLoadError(null);
    setEditing(false);
    setMessage(null);
    if (!practiceId) return;
    (async () => {
      try {
        const response = await fetch(docUrl(forPractice), { credentials: "include" });
        if (!response.ok) throw await errorFrom(response, "Could not load the coding standards.");
        const body = await response.json();
        if (currentPractice.current !== forPractice) return;
        setState({
          loaded: true,
          available: body.available !== false,
          doc: body.document,
          defaultTitle: body.default_title || "Coding Standards",
        });
      } catch (err) {
        if (currentPractice.current === forPractice) {
          setLoadError(err.message || "Could not load the coding standards.");
          setState((s) => ({ ...s, loaded: true }));
        }
      }
    })();
  }, [practiceId]);

  if (!practiceId) return null;
  const { loaded, available, doc, defaultTitle } = state;

  const openPdf = async () => {
    const tab = window.open("", "_blank");
    setMessage(null);
    try {
      const response = await fetch(docUrl(practiceId, "/pdf"), { credentials: "include" });
      if (!response.ok) throw await errorFrom(response, "Could not open the PDF.");
      const url = URL.createObjectURL(await response.blob());
      if (tab) tab.location.href = url;
      else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (tab) tab.close();
      setMessage({ type: "error", text: err.message || "Could not open the PDF." });
    }
  };

  const startEdit = () => {
    setDraft({ title: doc ? doc.title : defaultTitle, body: doc ? doc.body : "" });
    setMessage(null);
    setEditing(true);
  };

  const save = async () => {
    if (busy) return;
    const forPractice = practiceId;
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(docUrl(forPractice), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!response.ok) throw await errorFrom(response, "Could not save the coding standards.");
      const body = await response.json();
      if (currentPractice.current !== forPractice) return;
      const saved = body.document;
      const unchanged = doc && saved.version === doc.version;
      setState((s) => ({ ...s, doc: saved }));
      setEditing(false);
      setMessage({ type: "success", text: unchanged ? "No changes." : `Saved as version ${saved.version}.` });
    } catch (err) {
      if (currentPractice.current === forPractice) setMessage({ type: "error", text: err.message });
    } finally {
      if (currentPractice.current === forPractice) setBusy(false);
    }
  };

  const meta = doc
    ? [`Version ${doc.version}`, `updated ${fmtDate(doc.created_at)}`, doc.created_by].filter(Boolean).join(" · ")
    : null;

  return (
    <div className="integrations-section coding-standards" data-testid="coding-standards">
      <h3 className="practice-rules-section-title">Coding standards</h3>
      {message && <div className={`form-message ${message.type}`}>{message.text}</div>}
      {loadError && <p className="bs-error">{loadError}</p>}

      {loaded && !loadError && !editing && (
        <div className="cs-layout">
          <div className="cs-text">
            <p className="cs-explain">
              How Atlas codes this clinic's visits: the default codes for each visit type, when to add or change
              them, and what the note has to show for each one.
            </p>
            {!available && <p className="cs-meta">Not available yet.</p>}
            {available && !doc && <p className="cs-meta">No coding standards yet.</p>}
            {meta && <p className="cs-meta">{meta}</p>}
            <div className="cs-actions">
              {doc && (
                <button type="button" className="btn btn-secondary" onClick={openPdf}>
                  Open PDF
                </button>
              )}
              {canEdit && available && (
                <button type="button" className="btn btn-secondary" onClick={startEdit}>
                  {doc ? "Edit" : "Write them"}
                </button>
              )}
            </div>
          </div>
          {doc && (
            <button type="button" className="cs-doc" onClick={openPdf} aria-label={`Open ${doc.title} (PDF)`}>
              <span className="cs-doc-title">{doc.title}</span>
              {previewLines(doc.body).map((line, i) => (
                <span key={i} className="cs-doc-line">
                  {line}
                </span>
              ))}
              <span className="cs-doc-tag">PDF</span>
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className="cs-editor">
          <label className="cs-label" htmlFor="cs-title">
            Title
          </label>
          <input
            id="cs-title"
            className="bs-input cs-title-input"
            value={draft.title}
            maxLength={200}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
          <label className="cs-label" htmlFor="cs-body">
            Text
          </label>
          <textarea
            id="cs-body"
            className="cs-body-input"
            rows={24}
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          />
          <div className="cs-actions">
            <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
              {busy ? "Saving…" : `Save as version ${doc ? doc.version + 1 : 1}`}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
